/**
 * The arithmetic the HUD is allowed to do.
 *
 * Which is very little, and deliberately so. This module turns numbers that
 * have *already been decided elsewhere* into the numbers a browser needs to
 * paint them: a percentage for a fill, a string for a readout, a plural for a
 * badge. It knows nothing about experience curves, level-up thresholds,
 * regeneration or what a skill costs — every one of those is a rule, rules live
 * in `packages/core`, and a rule computed in a panel is the same bug as a damage
 * number computed in a scene (`CLAUDE.md`).
 *
 * It is a module of its own rather than a handful of expressions inside the
 * component for two reasons. The divide is the part that can be wrong — a zero
 * maximum is `NaN%`, a current above its maximum is an orb that paints outside
 * itself — and neither of those is visible in a rendered screenshot until it is
 * embarrassing. And `apps/web`'s unit runner has no DOM, so arithmetic that
 * lives here is provable directly rather than through markup.
 */

/** A number that is safe to put in front of a player. Anything else is zero. */
const finiteOrZero = (value: number): number => (Number.isFinite(value) ? value : 0);

/** A pool or a span, resolved for painting. */
export interface Fill {
  /** The current value as it will be shown — never below zero, never above `maximum`. */
  readonly current: number;
  /** The capacity as it will be shown — never below zero. */
  readonly maximum: number;
  /** `current / maximum`, in `0..1`. Zero for a pool with no capacity. */
  readonly ratio: number;
  /** The same thing in `0..100`, to two decimals, which is what a CSS length wants. */
  readonly percent: number;
}

/**
 * How full a pool is.
 *
 * The clamp is both-ended on purpose. A current above its maximum is an
 * ordinary thing for a client to be holding for a frame — a snapshot landing
 * beside a maximum that a buff has just changed — and the honest answer on
 * screen is a full orb, not one that overflows its own circle. A maximum of
 * zero is the case that actually crashes: `0 / 0` is `NaN`, and `height:NaN%`
 * is a fill a browser silently drops, so the orb looks full when the character
 * has nothing.
 */
export const fillOf = (current: number, maximum: number): Fill => {
  const capacity = Math.max(0, finiteOrZero(maximum));
  const held = Math.min(Math.max(0, finiteOrZero(current)), capacity);
  const ratio = capacity === 0 ? 0 : held / capacity;

  return { current: held, maximum: capacity, ratio, percent: Math.round(ratio * 10_000) / 100 };
};

/** `30 / 120` — the pool in whole points, for a player who wants the number. */
export const readoutOf = (fill: Fill): string => `${Math.round(fill.current)} / ${Math.round(fill.maximum)}`;

/**
 * What the experience bar is showing.
 *
 * Two states rather than one, because a maximum-level character is not a
 * character at 0% of the next level: the caller says the level spans no
 * experience, and there is no proportion to take. Presenting that as a bar
 * stuck at empty would read as "you have earned nothing", which is the opposite
 * of what it means.
 */
export type ExperienceBar =
  | { readonly kind: 'levelling'; readonly fill: Fill }
  | { readonly kind: 'max-level' };

/** The span this character is inside, or the news that there is not one. */
export const experienceBarOf = (progress: {
  readonly experienceIntoLevel: number;
  readonly experienceForNextLevel: number;
}): ExperienceBar =>
  // `> 0` and not `!== 0`: it also refuses a negative span and — because every
  // comparison with `NaN` is false — a span that is not a number at all.
  progress.experienceForNextLevel > 0
    ? { kind: 'levelling', fill: fillOf(progress.experienceIntoLevel, progress.experienceForNextLevel) }
    : { kind: 'max-level' };

/** The level as it is shown — a whole number, and never `Level NaN`. */
export const levelOf = (level: number): number => Math.max(0, Math.round(finiteOrZero(level)));

/** `1 skill point`, `3 stat points` — a badge a player can read without a legend. */
export const pointsLabel = (count: number, kind: 'skill' | 'stat'): string => {
  const whole = Math.floor(Math.max(0, finiteOrZero(count)));

  return `${whole} ${kind} point${whole === 1 ? '' : 's'}`;
};
